import React, {FC} from "react";
import {Box, Dialog, Typography, useTheme} from "@mui/material";

import {DtxSpacer} from "../../../../components/common/DtxSpacer";
import {DefaultButton} from "../../../../components/common/DefaultButton";
import {dataGridRowBorder} from "../../../../theme";
import {DialogHeading} from "../../AddAndUpdateSaas/dialogs/misc/styles";
import {LoadingSpinner} from "../../../../components/common/LoadingSpinner";
import {ISaasClientRestriction} from "../../../../services/SaasRestrictions/SaasRestrictionsQueries";

type propTypes = {
  integration: ISaasClientRestriction;
  isOpen: boolean;
  isSpinning: boolean;
  onCancel: () => void;
  onConfirm: (status: {saas: string, isDisabled: boolean}) => void;
};

// Asks the super admin to confirm enabling or disabling a SaaS
// integration for every client, explaining what that does
export const SetIntegrationStatusDialog: FC<propTypes> = ({
  integration,
  isOpen,
  isSpinning,
  onCancel,
  onConfirm,
  ...props
}) => {
  const theme = useTheme();

  // The status the integration is about to get
  const willDisable = !integration.isDisabled;
  const action = willDisable ? 'Disable' : 'Enable';

  return (
    <Dialog
      {...props}
      open={isOpen}
    >
      <Box
        sx={{
          border: dataGridRowBorder,
          borderRadius: theme.more.borderRadius,
          margin: 'auto',
          padding: theme.spacing(2),
          minWidth: '500px',
          maxWidth: '500px',
        }}
      >
        <DialogHeading>
          {`${action} ${integration.name}?`}
        </DialogHeading>
        {isSpinning ?
          <LoadingSpinner></LoadingSpinner> :
          <>
            <DtxSpacer space={2} />
            {willDisable ?
              <Typography>
                No client will be able to add <strong>{integration.name}</strong>,
                and the connections clients already have to it are hidden in the portal.
                Connections and their data are not deleted: enabling it again brings them back.
              </Typography> :
              <Typography>
                <strong>{integration.name}</strong> becomes available to add again
                {integration.restricted ? ' for the clients it is restricted to' : ' for all clients'},
                and the connections clients already have to it are shown in the portal again.
              </Typography>
            }
            <DtxSpacer space={4} />
            <Box sx={{textAlign: 'right'}}>
              <DefaultButton
                sx={{marginRight: theme.spacing()}}
                variant="contained"
                onClick={onCancel}
              >
                Cancel
              </DefaultButton>
              <DefaultButton
                sx={{marginRight: 0}}
                variant="contained"
                onClick={() => onConfirm({
                  saas: integration.identifier,
                  isDisabled: willDisable,
                })}
              >
                {action}
              </DefaultButton>
            </Box>
          </>
        }
      </Box>
    </Dialog>
  );
};
